import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const DeliveryOrderForm = () => {
  const [formData, setFormData] = useState({
    do_date: '',
    issuing_authority_id: '',
    do_number: '',
    warehouse_id: '',
    quantity_mt: '',
    sale_id: ''
  });
  // eslint-disable-next-line no-unused-vars
  const [buyers, setBuyers] = useState([]);
  const [selectedSaleOrder, setSelectedSaleOrder] = useState(null);
  const [quantityError, setQuantityError] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [commodities, setCommodities] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [warehouses, setWarehouses] = useState([]);

  const [issuingAuthorities, setIssuingAuthorities] = useState([]);
  const [saleOrders, setSaleOrders] = useState([]);
  const navigate = useNavigate();

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch commodities
        const commodityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          credentials: 'include'
        });
        if (commodityRes.ok) {
          const commodityData = await commodityRes.json();
          setCommodities(commodityData.data || []);
        }

        // Fetch Issuing Authorities
        const authorityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-issuing-authorities`, {
          credentials: 'include'
        });
        if (authorityRes.ok) {
          const authorityData = await authorityRes.json();
          setIssuingAuthorities(authorityData.data || []);
        }
        // Fetch Sale Orders
        const saleRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-sale-order`, {
          credentials: 'include'
        });
        if (saleRes.ok) {
          const saleData = await saleRes.json();
          setSaleOrders(saleData.data || []);
        }
        // Fetch Buyers
const buyerRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-domestic-buyers`, {
  credentials: 'include'
});
if (buyerRes.ok) {
  const buyerData = await buyerRes.json();
  setBuyers(buyerData.data || []);
}
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch all warehouses
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const warehouseRes = await fetch(`${API_BASE_URL}/api/masters/warehouses`, {
          credentials: 'include'
        });
        if (warehouseRes.ok) {
          const warehouseData = await warehouseRes.json();
          setWarehouses(warehouseData.data || []);
        }
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      }
    };
    fetchWarehouses();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate quantity when quantity field changes
    if (name === 'quantity_mt') {
      validateQuantity(value);
    }
  };

  const validateQuantity = (quantity) => {
    if (selectedSaleOrder && quantity && parseFloat(quantity) > parseFloat(selectedSaleOrder.remaining_qty_mt)) {
      setQuantityError(`Quantity cannot exceed available Sale Order quantity (${selectedSaleOrder.remaining_qty_mt} MT)`);
    } else {
      setQuantityError('');
    }
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    if (fieldName === 'sale_id') {
      const selectedOrder = saleOrders.find(so => so.sale_id === value);
      setSelectedSaleOrder(selectedOrder);
      setQuantityError(''); // Clear any previous quantity error
      
      if (selectedOrder) {
        setFormData(prev => ({
          ...prev,
          sale_id: value,
          warehouse_id: selectedOrder.warehouse_id || ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for quantity validation before submitting
    if (quantityError) {
      setMessage({ type: 'error', text: 'Please fix the quantity error before submitting' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sales/delivery-order-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Delivery Order Created Successfully!' });
        setFormData({
          do_date: '',
          issuing_authority_id: '',
          do_number: '',
          warehouse_id: '',
          quantity_mt: '',
          sale_id: ''
        });
        setSelectedSaleOrder(null);
        setQuantityError('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server Error' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <h2 className="text-[15px] font-bold mb-0 text-left">Delivery Order Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-3 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium mb-1">Sale Order</label>
              <SearchableDropdown
                options={saleOrders.map(so => ({ 
                  ...so, 
                  name: `ID: ${so.sale_id} - ${so.buyer_name} (${so.order_date ? new Date(so.order_date).toLocaleDateString() : 'N/A'}) - ${so.remaining_qty_mt} MT Avail`
                }))}
                value={formData.sale_id}
                onChange={val => handleDropdownChange('sale_id', val)}
                placeholder="Select Sale Order"
                displayKey="name"
                valueKey="sale_id"
                required
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Quantity (MT)</label>
              <input
                type="number"
                name="quantity_mt"
                value={formData.quantity_mt}
                onChange={handleChange}
                className={`w-full border px-3 py-1.5 text-[13px] bg-white focus:outline-none rounded-none ${
                  quantityError ? 'border-red-500' : 'border-gray-300 focus:border-[#15803d]'
                }`}
                step="0.001"
                required
              />
              {quantityError && (
                <div className="text-red-500 text-[12px] mt-1">{quantityError}</div>
              )}
              {selectedSaleOrder && !quantityError && (
                <div className="text-gray-500 text-[12px] mt-1">
                  Available quantity: {selectedSaleOrder.remaining_qty_mt} MT
                </div>
              )}
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">DO Date</label>
              <input
                type="date"
                name="do_date"
                value={formData.do_date}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">DO Number</label>
              <input
                type="text"
                name="do_number"
                value={formData.do_number}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Issuing Authority</label>
              <SearchableDropdown
                options={issuingAuthorities}
                value={formData.issuing_authority_id}
                onChange={val => handleDropdownChange('issuing_authority_id', val)}
                placeholder="Select Issuing Authority"
                displayKey="authority_name"
                valueKey="id"
                required
              />
            </div>


          </div>
        </div>
        <div className="px-3 pb-4">
          <button
            type="submit"
            className="bg-[#15803d] text-white px-4 py-1.5 rounded hover:bg-[#166534] font-semibold text-[13px]"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
          {message && (
            <span className={`ml-4 text-[13px] ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{message.text}</span>
          )}
        </div>
      </form>
    </div>
  );
};

export default DeliveryOrderForm;
